import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { usePermission } from '../../hooks/usePermission'; // ✅ Import Permission Hook
import { 
  CheckCircle, Clock, Shield, Activity, MapPin, 
  MessageSquare, FileText, Lock, LogIn, ShoppingCart
} from 'lucide-react';

const StaffDashboard = () => {
  const { can } = usePermission(); // ✅ Initialize Hook
  
  const { data: stats, isLoading } = useQuery({
    queryKey: ['staffStats'],
    queryFn: async () => {
      const res = await axiosClient.get('/admin/staff/dashboard-stats');
      return res.data;
    }
  });

  if (isLoading) return <div className="p-8 text-center text-slate-500">Loading your command center...</div>;

  // Helper: Icon Logic
  const getActionIcon = (action) => {
    switch(action) {
        case 'LOGIN': return <LogIn className="w-4 h-4 text-blue-500" />;
        case 'RESOLVE_ALERT': return <Shield className="w-4 h-4 text-green-500" />;
        case 'ASSIGN_TICKET': return <CheckCircle className="w-4 h-4 text-purple-500" />;
        case 'TICKET_REPLY': return <MessageSquare className="w-4 h-4 text-indigo-500" />;
        case 'CREATE_ORDER': return <FileText className="w-4 h-4 text-amber-500" />;
        case 'CREATE_COUPON': return <ShoppingCart className="w-4 h-4 text-pink-500" />;
        default: return <Activity className="w-4 h-4 text-slate-400" />;
    }
  };

  const formatTime = (isoString) => new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const formatDate = (isoString) => new Date(isoString).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* 1. Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Staff Command Center</h1>
          <p className="text-slate-500 text-sm mt-1">
             Session ID: <span className="font-mono text-blue-600">#{Math.floor(Math.random()*90000) + 10000}</span>
          </p>
        </div>
        <div className="flex items-center gap-4 text-xs font-mono text-slate-500 bg-slate-50 px-4 py-2 rounded-lg border border-slate-100">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/> {stats?.last_login_ip}</span>
            <span className="h-3 w-px bg-slate-300"></span>
            <span className="flex items-center gap-1 text-green-600"><Lock className="w-3 h-3"/> Active</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* 2. LEFT COLUMN: DYNAMIC WIDGETS */}
        <div className="space-y-6">
            
            <div className="grid grid-cols-2 gap-4">
                
                {/* ✅ WIDGET 1: SHOW ONLY IF CAN VIEW TICKETS */}
                {can('tickets:view') ? (
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-16 h-16 bg-purple-50 rounded-bl-full -mr-4 -mt-4"></div>
                        <div className="text-slate-400 text-xs font-bold uppercase mb-2 relative z-10">Assigned Tasks</div>
                        <div className="text-3xl font-black text-slate-800 relative z-10">{stats?.assigned_tickets_open}</div>
                        <div className="text-xs text-purple-600 mt-1 font-medium relative z-10">Open Tickets</div>
                    </div>
                ) : (
                    // Fallback for Non-Support Staff (e.g. Accountant)
                    <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                        <div className="text-slate-400 text-xs font-bold uppercase mb-2">Role Access</div>
                        <div className="text-lg font-bold text-slate-700">Restricted</div>
                        <div className="text-xs text-slate-400 mt-1">No Ticket Access</div>
                    </div>
                )}

                {/* ✅ WIDGET 2: GLOBAL THROUGHPUT (For Everyone) */}
                <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-green-50 rounded-bl-full -mr-4 -mt-4"></div>
                    <div className="text-slate-400 text-xs font-bold uppercase mb-2 relative z-10">My Activity</div>
                    <div className="text-3xl font-black text-slate-800 relative z-10">{stats?.actions_today}</div>
                    <div className="text-xs text-green-600 mt-1 font-medium relative z-10">Actions Today</div>
                </div>
            </div>

            {/* ✅ WIDGET 3: PERFORMANCE CONTEXT */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 rounded-xl text-white shadow-lg">
                <h3 className="font-bold text-lg mb-4 flex items-center"><Activity className="w-5 h-5 mr-2 text-blue-400"/> Performance</h3>
                <div className="space-y-4">
                    
                    {/* Only show Ticket Stats if they have Ticket access */}
                    {can('tickets:view') && (
                        <div>
                            <div className="flex justify-between text-xs mb-1 text-slate-300">
                                <span>Tickets Closed (All Time)</span>
                                <span className="font-bold text-white">{stats?.assigned_tickets_closed}</span>
                            </div>
                            <div className="w-full bg-slate-700 rounded-full h-1.5">
                                <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${Math.min(stats?.assigned_tickets_closed * 2, 100)}%` }}></div>
                            </div>
                        </div>
                    )}

                    {/* Show Order Context for Accountants/Admins */}
                    {can('orders:view') && !can('tickets:view') && (
                        <div>
                            <div className="flex justify-between text-xs mb-1 text-slate-300">
                                <span>Commerce Actions</span>
                                <span className="font-bold text-white">Active</span>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">Tracking order generation and billing updates.</p>
                        </div>
                    )}
                    
                    <div className="pt-4 border-t border-slate-700">
                        <p className="text-xs text-slate-400 leading-relaxed">
                            System logging is active. Your IP <span className="text-white font-mono">{stats?.last_login_ip}</span> is whitelisted for this session.
                        </p>
                    </div>
                </div>
            </div>
        </div>

        {/* 3. RIGHT COLUMN: ACTIVITY TIMELINE (Universal) */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-[600px]">
            <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                <h3 className="font-bold text-slate-700 flex items-center">
                    <Clock className="w-4 h-4 mr-2 text-slate-400"/> Your Timeline
                </h3>
                <span className="text-xs text-slate-400 font-mono bg-white px-2 py-1 rounded border border-slate-200">Recent Actions</span>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 relative custom-scrollbar"> {/* ✅ Applied Custom Scrollbar Here Too */}
                <div className="absolute left-9 top-6 bottom-6 w-px bg-slate-200"></div>

                <div className="space-y-8">
                    {stats?.recent_activity?.length === 0 && (
                        <div className="text-center text-slate-400 py-10 pl-4">No recent activity found.</div>
                    )}

                    {stats?.recent_activity?.map((log, idx) => (
                        <div key={idx} className="relative flex gap-6 group">
                            {/* Icon Dot */}
                            <div className="relative z-10 w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform group-hover:border-blue-300">
                                {getActionIcon(log.action)}
                            </div>

                            {/* Content */}
                            <div className="flex-1 -mt-1">
                                <div className="flex justify-between items-start">
                                    <h4 className="text-sm font-bold text-slate-800">{log.action.replace(/_/g, ' ')}</h4>
                                    <span className="text-xs text-slate-400 font-mono whitespace-nowrap">{formatDate(log.created_at)} {formatTime(log.created_at)}</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                    {log.entity_type.toUpperCase()} #{log.entity_id || 'N/A'}
                                </p>
                                
                                {log.details && Object.keys(log.details).length > 0 && (
                                    <div className="mt-2 bg-slate-50 p-2 rounded border border-slate-100 text-[10px] font-mono text-slate-600 overflow-x-auto custom-scrollbar">
                                        {Object.entries(log.details).map(([k, v]) => (
                                            <span key={k} className="mr-3">
                                                <span className="text-slate-400">{k}:</span> {String(v)}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

      </div>
    </div>
  );
};

export default StaffDashboard;